import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Copy, Loader2, Reply, Replace, Send, Sparkles, TextCursorInput, Trash2 } from 'lucide-react';
import { Button, Select } from '../../../components/ui';
import MarkdownContent from '../../../components/MarkdownContent';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { cn } from '../../../lib/utils';
import AiAssistantProgressPanel from './AiAssistantProgressPanel';
import SmartReplyTemplatesPanel from './SmartReplyTemplatesPanel';
import { isValidEmailAiThreadId } from './ai-assistant/emailAiAssistant.utils';
import type { AiModelOption } from '../../configuration-settings';

export interface EmailAiPromptListItem {
  id: number;
  code: string;
  name: string;
  needAdditionalText?: boolean;
  needSelectedText?: boolean;
}

export interface EmailAiThreadMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

interface ThreadBubbleProps {
  msg: EmailAiThreadMessage;
  compact?: boolean;
}

const ThreadBubble: React.FC<ThreadBubbleProps> = ({ msg, compact }) => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const [copied, setCopied] = useState(false);
  const isUser = msg.role === 'user';

  const handleCopy = useCallback(async () => {
    const ok = await copyToClipboard(msg.content);
    if (ok) {
      setCopied(true);
      showSuccess('Copied to clipboard');
      window.setTimeout(() => setCopied(false), 2000);
    } else {
      showError('Copy not available');
    }
  }, [msg.content, showError, showSuccess]);

  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'group relative rounded-2xl text-sm shadow-sm',
          compact ? 'max-w-full px-3 py-2.5 pr-9' : 'max-w-[92%] px-4 py-3 pr-10',
          isUser
            ? 'bg-indigo-600 text-white rounded-br-md'
            : cn(colors.surface, colors.border, 'border rounded-bl-md', colors.text),
        )}
      >
        {isUser ? (
          <MarkdownContent
            content={msg.content}
            className={cn(
              compact ? 'text-xs sm:text-sm' : 'text-sm',
              'prose-invert [&_*]:text-white [&_strong]:text-white [&_hr]:border-white/30',
            )}
          />
        ) : (
          <MarkdownContent content={msg.content} className={compact ? 'text-xs sm:text-sm' : 'text-sm'} />
        )}

        {!isUser ? (
          <button
            type="button"
            onClick={() => void handleCopy()}
            className={cn(
              'absolute top-1.5 right-1.5 rounded-lg p-1.5 transition-all',
              'opacity-70 hover:opacity-100 sm:opacity-60 sm:group-hover:opacity-100',
              colors.textSecondary,
              'hover:bg-black/[0.06] dark:hover:bg-white/[0.08]',
            )}
            title="Copy"
            aria-label="Copy message"
          >
            {copied ? (
              <Check className="h-4 w-4" strokeWidth={2} aria-hidden />
            ) : (
              <Copy className="h-4 w-4" strokeWidth={1.75} aria-hidden />
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
};

interface LastReplyActionsProps {
  content: string;
  compact?: boolean;
  canEditDraft: boolean;
  isRead: boolean;
  onCopy: () => void;
  onCreateReply?: (text: string) => void;
  onInsert?: (text: string) => void;
  onReplace?: (text: string) => void;
}

const LastReplyActions: React.FC<LastReplyActionsProps> = ({
  content,
  compact,
  canEditDraft,
  isRead,
  onCopy,
  onCreateReply,
  onInsert,
  onReplace,
}) => {
  const { colors } = useTheme();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    onCopy();
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const iconBtnClass = cn(
    'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors',
    colors.border,
    colors.textSecondary,
    colors.interactiveHover,
  );

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-1.5 border-t px-2 py-1.5',
        colors.border,
        'bg-indigo-500/[0.04] dark:bg-indigo-500/[0.08]',
        compact && 'px-2.5 py-2 gap-2',
      )}
    >
      <span className={cn('mr-auto text-[10px] font-semibold uppercase tracking-wider', colors.textMuted)}>
        Last reply
      </span>
      <button
        type="button"
        onClick={() => void handleCopy()}
        className={iconBtnClass}
        title="Copy to clipboard"
        aria-label="Copy last reply"
      >
        {copied ? <Check className="h-4 w-4" strokeWidth={2} aria-hidden /> : <Copy className="h-4 w-4" strokeWidth={1.75} aria-hidden />}
      </button>
      {isRead && onCreateReply ? (
        <Button
          type="button"
          variant="default"
          size="sm"
          className={cn('gap-1.5', compact && 'h-8 px-2.5 text-xs')}
          onClick={() => onCreateReply(content)}
        >
          <Reply className="h-3.5 w-3.5" aria-hidden />
          Create reply
        </Button>
      ) : null}
      {canEditDraft && onInsert ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn('gap-1.5', compact && 'h-8 px-2.5 text-xs')}
          onClick={() => onInsert(content)}
        >
          <TextCursorInput className="h-3.5 w-3.5" aria-hidden />
          Insert
        </Button>
      ) : null}
      {canEditDraft && onReplace ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn('gap-1.5', compact && 'h-8 px-2.5 text-xs')}
          onClick={() => onReplace(content)}
        >
          <Replace className="h-3.5 w-3.5" aria-hidden />
          Replace
        </Button>
      ) : null}
    </div>
  );
};

export interface EmailAiConversationProps {
  compact?: boolean;
  loading: boolean;
  runningPromptName?: string;
  onCancelLoading?: () => void;
  prompts: EmailAiPromptListItem[];
  models: AiModelOption[];
  selectedModelId: string;
  onModelChange: (id: string) => void;
  selectedPromptId: number | null;
  onSelectPrompt: (id: number) => void;
  needAdditional: boolean;
  needSelected: boolean;
  canRunPrompt: boolean;
  additionalText: string;
  onAdditionalTextChange: (value: string) => void;
  selectedPromptCode?: string;
  thread: EmailAiThreadMessage[];
  chatInput: string;
  onChatInputChange: (value: string) => void;
  onRunPrompt: () => void;
  onSendMessage: () => void;
  onClearThread: () => void;
  canEditDraft: boolean;
  onInsertAtCursor?: (text: string) => void;
  onReplaceBody?: (text: string) => void;
  onCreateReply?: (text: string) => void;
  isRead: boolean;
  selectedLen: number;
  emailId?: number;
  partitionAt?: string | null;
  clientId?: number;
}

const EmailAiConversation: React.FC<EmailAiConversationProps> = (props) => {
  const {
    compact,
    loading,
    runningPromptName,
    onCancelLoading,
    prompts,
    models,
    selectedModelId,
    onModelChange,
    selectedPromptId,
    onSelectPrompt,
    needAdditional,
    needSelected,
    canRunPrompt,
    additionalText,
    onAdditionalTextChange,
    selectedPromptCode,
    thread,
    chatInput,
    onChatInputChange,
    onRunPrompt,
    onSendMessage,
    onClearThread,
    canEditDraft,
    onInsertAtCursor,
    onReplaceBody,
    onCreateReply,
    isRead,
    selectedLen,
    emailId,
    partitionAt,
    clientId,
  } = props;

  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const lastAssistantMessage = [...thread].reverse().find((m) => m.role === 'assistant');
  const lastAssistantContent = lastAssistantMessage?.content?.trim() ?? '';

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread, loading]);

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!loading && chatInput.trim()) onSendMessage();
    }
  };

  const plainToHtml = (text: string) => {
    if (!text.trim()) return '<p><br></p>';
    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br/>');
    return `<p>${escaped}</p>`;
  };

  const handleInsert = (text: string) => {
    if (!onInsertAtCursor) return;
    const html = text.includes('<') ? text : plainToHtml(text);
    onInsertAtCursor(html);
  };

  const handleReplace = (text: string) => {
    if (!onReplaceBody) return;
    const html = text.includes('<') ? text : plainToHtml(text);
    onReplaceBody(html);
  };

  const handleCopyLastReply = useCallback(async () => {
    if (!lastAssistantContent) return;
    const ok = await copyToClipboard(lastAssistantContent);
    if (ok) showSuccess('Copied to clipboard');
    else showError('Copy not available');
  }, [lastAssistantContent, showError, showSuccess]);

  const sectionLabel = cn('text-[10px] font-semibold uppercase tracking-wider', colors.textMuted);

  const showSmartReplies = isValidEmailAiThreadId(emailId);

  const smartRepliesInChat = showSmartReplies ? (
    <SmartReplyTemplatesPanel
      layout="chat"
      compact={compact}
      emailId={emailId}
      partitionAt={partitionAt}
      clientId={clientId}
      onUseTemplate={onInsertAtCursor ? handleInsert : undefined}
      onCreateReply={onCreateReply}
    />
  ) : null;

  const contextHint = isRead
    ? 'Ask follow-ups or refine the reply'
    : selectedLen > 0
      ? `${selectedLen} chars selected in draft`
      : 'Select text in the draft for rewrite prompts';

  const chatScroll = (
    <div
      ref={scrollRef}
      className={cn('flex-1 overflow-y-auto', compact ? 'px-2.5 py-2 space-y-2' : 'p-4 space-y-3')}
    >
      {smartRepliesInChat}

      {thread.length === 0 && !loading && !showSmartReplies ? (
        <div className={cn('flex flex-col items-center justify-center py-8 text-center', colors.textMuted)}>
          <Sparkles className="mb-2 h-7 w-7 text-indigo-400/80" aria-hidden />
          <p className={cn('text-sm font-medium', colors.textSecondary)}>Start a conversation</p>
          <p className={cn('mt-1 max-w-xs leading-relaxed', compact ? 'text-[11px]' : 'text-xs')}>
            Pick a quick prompt or type below.
          </p>
        </div>
      ) : null}

      {thread.length === 0 && !loading && showSmartReplies ? (
        <p className={cn('text-center text-xs', colors.textMuted)}>
          Or run a quick prompt from the left — follow-ups appear here.
        </p>
      ) : null}

      {thread.map((msg) => (
        <ThreadBubble key={msg.id} msg={msg} compact={compact} />
      ))}

      {loading ? (
        <div className="space-y-2">
          <AiAssistantProgressPanel promptName={runningPromptName} compact={compact} />
          {onCancelLoading ? (
            <div className={cn('flex justify-center', compact ? 'pb-1' : 'pb-2')}>
              <Button type="button" variant="outline" size="sm" onClick={onCancelLoading} className="text-xs">
                Cancel
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  const chatInputArea = (
    <div
      className={cn(
        'shrink-0 border-t',
        colors.border,
        'bg-black/[0.02] dark:bg-white/[0.03]',
        compact ? 'p-2' : 'p-3',
      )}
    >
      <div className="flex gap-2">
        <textarea
          ref={inputRef}
          value={chatInput}
          onChange={(e) => onChatInputChange(e.target.value)}
          onKeyDown={handleInputKeyDown}
          disabled={loading}
          rows={compact ? 2 : 3}
          placeholder="Ask AI… (Enter to send)"
          className={cn(
            'flex-1 resize-y rounded-xl border leading-relaxed',
            compact ? 'min-h-[3.25rem] px-2.5 py-2 text-xs' : 'min-h-[4.5rem] px-3 py-2.5 text-sm',
            colors.text,
            colors.border,
            colors.surface,
          )}
        />
        <Button
          type="button"
          variant="default"
          className={cn('shrink-0 self-end', compact ? 'h-8 w-8 p-0' : 'h-10 px-4 gap-1.5')}
          onClick={onSendMessage}
          disabled={loading || !chatInput.trim()}
          title="Send message"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Send className="h-4 w-4" aria-hidden />
          )}
          {!compact ? <span className="sr-only sm:not-sr-only sm:inline">Send</span> : null}
        </Button>
      </div>
    </div>
  );

  const lastReplyBar =
    lastAssistantContent && !loading ? (
      <LastReplyActions
        content={lastAssistantContent}
        compact={compact}
        canEditDraft={canEditDraft}
        isRead={isRead}
        onCopy={() => void handleCopyLastReply()}
        onCreateReply={onCreateReply}
        onInsert={onInsertAtCursor ? handleInsert : undefined}
        onReplace={onReplaceBody ? handleReplace : undefined}
      />
    ) : null;

  if (compact) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className={cn('shrink-0 space-y-2 border-b px-2.5 py-2', colors.border, 'bg-black/[0.02] dark:bg-white/[0.02]')}>
          {models.length > 0 ? (
            <Select
              name="email-ai-model"
              value={selectedModelId}
              onChange={(e) => onModelChange(String(e.target.value))}
              options={models.map((m) => ({ value: m.id, label: m.label }))}
              placeholder="Model"
              showClearButton={false}
              searchPlaceholder="Search models…"
              className="w-full"
              disabled={loading}
            />
          ) : null}

          <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-0.5 [scrollbar-width:thin]">
            {prompts.map((p) => {
              const active = selectedPromptId === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onSelectPrompt(p.id)}
                  disabled={loading}
                  className={cn(
                    'shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-all',
                    active
                      ? 'border-indigo-500 bg-indigo-600 text-white shadow-sm'
                      : cn(colors.border, colors.text, 'hover:border-indigo-400/60 hover:bg-indigo-500/5'),
                  )}
                >
                  {p.name}
                </button>
              );
            })}
          </div>

          {needAdditional ? (
            <textarea
              value={additionalText}
              onChange={(e) => onAdditionalTextChange(e.target.value)}
              disabled={loading}
              rows={2}
              placeholder={
                selectedPromptCode === 'prompt_reply_custom_command'
                  ? 'Your instruction…'
                  : 'Extra context…'
              }
              className={cn(
                'w-full resize-y rounded-lg border px-2 py-1.5 text-[11px]',
                colors.text,
                colors.border,
                'bg-transparent',
              )}
            />
          ) : null}

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="default"
              size="sm"
              className="h-8 flex-1 gap-1.5 text-xs"
              onClick={onRunPrompt}
              disabled={loading || !canRunPrompt || !selectedPromptId}
            >
              <Sparkles className="h-3.5 w-3.5" aria-hidden />
              Run prompt
            </Button>
            {thread.length > 0 ? (
              <button
                type="button"
                onClick={onClearThread}
                disabled={loading}
                className={cn(
                  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors',
                  colors.border,
                  colors.textMuted,
                  colors.interactiveHover,
                )}
                title="Clear conversation"
                aria-label="Clear conversation"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </button>
            ) : null}
          </div>

          <p className={cn('text-[10px] leading-snug', colors.textMuted)}>
            {needSelected && !canRunPrompt ? 'Select text in the email first.' : contextHint}
          </p>
        </div>

        {chatScroll}
        {lastReplyBar}
        {chatInputArea}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
      <div
        className={cn(
          'shrink-0 border-b lg:border-b-0 lg:border-r lg:w-60',
          colors.border,
          'bg-black/[0.02] dark:bg-white/[0.02]',
        )}
      >
        <div className="flex max-h-[min(88vh,720px)] flex-col space-y-3 overflow-y-auto p-3">
          {models.length > 0 ? (
            <div>
              <p className={cn(sectionLabel, 'mb-1.5')}>Model</p>
              <Select
                name="email-ai-model"
                value={selectedModelId}
                onChange={(e) => onModelChange(String(e.target.value))}
                options={models.map((m) => ({ value: m.id, label: m.label }))}
                placeholder="Choose model"
                showClearButton={false}
                searchPlaceholder="Search…"
                className="w-full"
                disabled={loading}
              />
            </div>
          ) : null}

          <div>
            <p className={cn(sectionLabel, 'mb-1.5')}>Quick prompts</p>
            <div className="grid gap-1">
              {prompts.map((p) => {
                const active = selectedPromptId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => onSelectPrompt(p.id)}
                    disabled={loading}
                    className={cn(
                      'rounded-lg border px-2.5 py-2 text-left text-xs transition-all',
                      active
                        ? 'border-indigo-500/70 bg-indigo-500/10 ring-1 ring-indigo-500/25'
                        : cn(colors.border, 'hover:bg-black/[0.04] dark:hover:bg-white/[0.05]'),
                      colors.text,
                    )}
                  >
                    {p.name}
                  </button>
                );
              })}
            </div>
          </div>

          {needAdditional ? (
            <div>
              <label className={cn('mb-1 block text-xs font-medium', colors.textMuted)}>
                {selectedPromptCode === 'prompt_reply_custom_command' ? 'Instruction' : 'Extra context'}
              </label>
              <textarea
                value={additionalText}
                onChange={(e) => onAdditionalTextChange(e.target.value)}
                disabled={loading}
                rows={2}
                placeholder="Tell the AI what you need…"
                className={cn(
                  'w-full resize-y rounded-lg border px-2.5 py-2 text-xs',
                  colors.text,
                  colors.border,
                  'bg-transparent',
                )}
              />
            </div>
          ) : null}

          {needSelected && !canRunPrompt ? (
            <p className={cn('text-[11px]', colors.textMuted)}>Select text in the email first.</p>
          ) : null}

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full gap-1.5"
            onClick={onRunPrompt}
            disabled={loading || !canRunPrompt || !selectedPromptId}
          >
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            Run prompt
          </Button>
        </div>
      </div>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className={cn('flex items-center justify-between gap-2 border-b px-3 py-2', colors.border)}>
          <p className={cn('text-xs', colors.textMuted)}>{contextHint}</p>
          {thread.length > 0 ? (
            <button
              type="button"
              onClick={onClearThread}
              disabled={loading}
              className={cn(
                'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors',
                colors.textMuted,
                colors.interactiveHover,
              )}
              title="Clear conversation"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
              Clear
            </button>
          ) : null}
        </div>

        {chatScroll}
        {lastReplyBar}
        {chatInputArea}
      </div>
    </div>
  );
};

export default EmailAiConversation;
